import { zodResolver } from '@hookform/resolvers/zod'
import { ChangeEvent, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { handlePhonePaste } from '@shared/__HACKS'
import Button from '@shared/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@shared/ui/form'
import Input from '@shared/ui/input'
import MaskedInput from '@shared/ui/input-mask'
import Switch from '@shared/ui/switch'
import { PHONE_MASK_LENGTH } from '@shared/validation'

import styles from './AddressForm.module.scss'

interface AddressFormProps {
  isLoading?: boolean;
  onSubmit: (values: z.infer<typeof formSchema>) => void;
  isZone: boolean;
  initialValues?: Partial<FormValues>;
  editingAddressId?: string;
}

export interface FormValues {
  apartment?: string;
  floor?: string;
  entrance?: string;
  intercom?: string;
  comment?: string;
  title?: string;
  otherBuyer?: boolean;
  otherBuyerName?: string;
  otherBuyerPhone?: string;
}

const formSchema = z
  .object({
    apartment: z.string().optional(),
    floor: z.string().optional(),
    entrance: z.string().optional(),
    intercom: z.string().optional(),
    comment: z.string().optional(),
    title: z.string().optional(),
    otherBuyer: z.boolean().optional(),
    otherBuyerName: z.string().optional(),
    otherBuyerPhone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.otherBuyer) {
      if (!data.otherBuyerName?.trim()) {
        ctx.addIssue({
          path: [ 'otherBuyerName' ],
          code: z.ZodIssueCode.custom,
          message: 'Обязательное поле',
        })
      }
      if ((data.otherBuyerPhone?.trim().length ?? 0) < PHONE_MASK_LENGTH) {
        ctx.addIssue({
          path: [ 'otherBuyerPhone' ],
          code: z.ZodIssueCode.custom,
          message: 'Неверный номер',
        })
      }
    }
  })

export default function AddressForm ({
  isLoading,
  onSubmit,
  isZone,
  initialValues
}: AddressFormProps) {
  const [ isPrivateHouse, setIsPrivateHouse ] = useState(false)
  const [ showTitleInput, setShowTitleInput ] = useState(!!initialValues?.title)
  const [ otherBuyer, setOtherBuyer ] = useState(!!initialValues?.otherBuyer)
  const { t: tc } = useTranslation('common')
  const { t: to } = useTranslation('order')

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      apartment: initialValues?.apartment ?? '',
      floor: initialValues?.floor ?? '',
      entrance: initialValues?.entrance ?? '',
      intercom: initialValues?.intercom ?? '',
      comment: initialValues?.comment ?? '',
      title: initialValues?.title ?? '',
      otherBuyer: initialValues?.otherBuyer ?? false,
      otherBuyerName: initialValues?.otherBuyerName ?? '',
      otherBuyerPhone: initialValues?.otherBuyerPhone ?? '',
    },
  })

  return (
    <div className={styles.root}>
      <Switch
        classNameRoot={styles.root__switch}
        checked={isPrivateHouse}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setIsPrivateHouse(e.target.checked)
        }
      >
        {to('form.private-house')}
      </Switch>
      <Form {...form}>
        <form
          className={styles.root__form}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {!isPrivateHouse && (
            <div className={styles.root__group}>
              <FormField
                control={form.control}
                name="apartment"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={to('form.apartment')}
                        inputSize="large"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="floor"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={to('form.floor')}
                        inputSize="large"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="entrance"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={to('form.entrance')}
                        inputSize="large"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="intercom"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={to('form.intercom')}
                        inputSize="large"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={to('order.comment')}
                    inputSize="large"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className={styles.root__parents}>
            <Button
              className={styles['root__parents-button']}
              size="small"
              theme="grey"
              type="button"
              onClick={() => setShowTitleInput(!showTitleInput)}
            >
              <span className={styles['root__parents-button-icon']}></span>
              {to('address_label')}
            </Button>
            {showTitleInput && (
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={to('order.title')}
                        inputSize="large"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
          <Switch
            classNameRoot={styles.root__switch}
            checked={otherBuyer}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setOtherBuyer(e.target.checked)
              form.setValue('otherBuyer', e.target.checked)
            }}
          >
            {to('order.otherBuyer')}
          </Switch>
          {otherBuyer && (
            <>
              <FormField
                control={form.control}
                name="otherBuyerName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={to('order.otherBuyerName')}
                        inputSize="large"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="otherBuyerPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <MaskedInput
                        {...field}
                        placeholder="+7 000 000 00 00"
                        inputSize="large"
                        onPaste={handlePhonePaste}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          <Button
            className={styles.root__submit}
            type="submit"
            size="large"
            disabled={isLoading || !isZone}
          >
            {tc('save')}
          </Button>
        </form>
      </Form>
    </div>
  )
}
